import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { extractErrorMessage } from "../../api/client";
import { getWorkflowReportPdf, WorkflowDetail } from "../../api/workflows";

// Report panel — a link to the PDF the trailing report stage wrote at
// <output>/_report.pdf.
//
// Deliberately not a renderer. ManifestPanel draws manifest/v1 itself
// because the manifest is JSON the portal can lay out; the report is a
// PDF and the PDF is the artefact, so re-implementing its sections in
// React would be a second renderer to keep in step with the first for
// no new information. Anything finer than what the report shows is a
// query against _demographics.parquet, which ships in the download
// bundle.
//
// Hidden unless the export actually asked for a report — it is opt-in
// at submit time, so most exports have no PDF and an empty panel on
// every one of them would be noise.

interface Props {
  wf: WorkflowDetail;
}

const EXPORT_KINDS = new Set(["batch_export", "month_export", "year_export"]);

export function ReportPanel({ wf }: Props) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const requested = useMemo(() => {
    try {
      const params = JSON.parse(wf.params_json || "{}");
      return Boolean(params.generate_report);
    } catch {
      return false;
    }
  }, [wf.params_json]);

  // Same eligibility rules as the manifest, plus the opt-in. The
  // ``output_deleted_at`` check matters for the same reason: the report
  // lived at the output prefix, so the reaper took it, and the fetch
  // would come back 410.
  const eligible =
    requested &&
    EXPORT_KINDS.has(wf.kind) &&
    wf.status === "succeeded" &&
    !wf.output_deleted_at;

  useEffect(() => {
    if (!eligible) {
      setUrl(null);
      return;
    }
    let cancelled = false;
    let objectUrl: string | null = null;
    setLoading(true);
    setError(null);
    getWorkflowReportPdf(wf.id)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch((err) => {
        if (!cancelled) setError(extractErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      // Revoking on unmount rather than leaving it to the page teardown:
      // the blob is held in memory until the URL is released, and a user
      // clicking between exports would otherwise accumulate one PDF per
      // visit for the life of the tab.
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [wf.id, eligible]);

  const handleDownload = useCallback(() => {
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `paim-report-${wf.id.slice(0, 8)}.pdf`;
    a.click();
  }, [url, wf.id]);

  if (!eligible) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        rowGap={1}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <PictureAsPdfOutlinedIcon color="action" />
          <div>
            <Typography variant="overline" color="text.secondary">
              Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Month-by-month audience and demographics over this export.
            </Typography>
          </div>
        </Stack>
        {loading && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={18} />
            <Typography variant="caption">fetching…</Typography>
          </Stack>
        )}
        {url && (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              href={url}
              target="_blank"
              rel="noopener"
            >
              Open
            </Button>
            <Button size="small" variant="contained" onClick={handleDownload}>
              Download
            </Button>
          </Stack>
        )}
      </Stack>
      {error && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Paper>
  );
}
